/**
 * Supabase Client Configuration
 *
 * This file creates a singleton Supabase client for the frontend.
 * Uses the anon key which is safe to expose in browser code.
 * Row Level Security (RLS) policies protect user data.
 */

import { createClient } from '@supabase/supabase-js';

// Validate environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env file:\n' +
    '  VITE_SUPABASE_URL=your_project_url\n' +
    '  VITE_SUPABASE_ANON_KEY=your_anon_key'
  );
}

/**
 * Supabase client singleton
 *
 * Security features:
 * - Uses anon key (safe for client-side)
 * - Respects Row Level Security policies
 * - Auto-refreshes auth tokens
 * - Persists session in localStorage
 */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Persist auth session in localStorage
    autoRefreshToken: true,
    persistSession: true,
    // We manually process the auth callback hash, so disable auto-detection
    detectSessionInUrl: false,

    // Storage key for auth tokens
    storageKey: 'arber-auth-token',

    // Use localStorage for session persistence
    storage: window.localStorage,

    // CRITICAL: Add flow type to prevent hanging
    flowType: 'pkce',
  },
});

/**
 * Auth initialization is handled automatically by Supabase via onAuthStateChange.
 * This function is kept for backwards compatibility but does nothing blocking.
 *
 * The onAuthStateChange listener fires immediately with the cached session
 * from localStorage, so we don't need to wait for getSession() which can
 * hang during token refresh on slow networks.
 */
export async function ensureAuthReady(): Promise<void> {
  // No-op - onAuthStateChange handles session restoration automatically
  // We don't call getSession() here because it can hang during token refresh
  return;
}

/**
 * Database type definitions
 * These will be auto-generated from your Supabase schema
 */
export interface Database {
  public: {
    Tables: {
      user_profiles: {
        Row: {
          id: string;
          email: string;
          full_name: string | null;
          avatar_url: string | null;
          subscription_tier: 'free' | 'pro' | 'premium';

          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['user_profiles']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['user_profiles']['Insert']>;
      };
      user_preferences: {
        Row: {
          id: string;
          user_id: string;
          favorite_sports: string[];
          favorite_bookmakers: string[];
          preferred_regions: string[];
          preferred_markets: string[];
          default_view: 'arbitrage' | 'ev' | 'player-props';
          default_odds_format: 'american' | 'decimal' | 'fractional';
          round_bet_amounts: boolean;
          email_notifications: boolean;
          min_profit_alert: number;
          min_ev_alert: number;
          created_at: string;
          updated_at: string;
        };
      };

    };
  };
}
